import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { X } from 'lucide-react'
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { SendLetterPanel } from '@/components/biddle/SendLetterPanel'
import { useAuth } from '@/hooks/use-auth'
import { formatAddressDisplay } from '@/lib/address'
import { closeBiddleDetail } from '@/lib/biddle-navigation'
import { callableErrorMessage } from '@/lib/callable-errors'
import {
  createBuyerOwnerChat,
  deleteBiddle,
  type ChatBiddlePayload,
} from '@/lib/callables'
import { db } from '@/lib/firebase'
import { openSignIn } from '@/lib/signin-navigation'
import { formatAud, formatDeposit, formatSettlement, relativeTime } from '@/lib/utils'
import {
  biddleStatus,
  isBiddleActive,
  parseBiddle,
  parseProperty,
  type Biddle,
  type Property,
} from '@/types/models'

function toChatBiddlePayload(biddle: Biddle): ChatBiddlePayload {
  return {
    id: biddle.id,
    propertyID: biddle.propertyID,
    price: biddle.price,
    settlement: biddle.settlement,
    deposit: biddle.deposit,
    notes: biddle.notes,
    expiry: biddle.expiry ? Math.floor(biddle.expiry.getTime() / 1000) : null,
    isOpenOffer: biddle.isOpenOffer,
    timeCreated: Math.floor(biddle.timeCreated.getTime() / 1000),
    ownerHasViewed: true,
    ownerHasAccepted: true,
    buyerBiddleTag: biddle.buyerBiddleTag,
    ownerBiddleTag: biddle.ownerBiddleTag,
  }
}

/**
 * Biddle detail overlay — opened via `?biddle=` + `?biddleProperty=` on any shell route.
 */
export function BiddleDetailModal() {
  const [searchParams] = useSearchParams()
  const biddleId = searchParams.get('biddle')
  const propertyId = searchParams.get('biddleProperty')
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, isGuest, isLoading: authLoading } = useAuth()

  const [biddle, setBiddle] = useState<Biddle | null>(null)
  const [property, setProperty] = useState<Property | null>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const signedIn = Boolean(user) && !isGuest

  useEffect(() => {
    if (!biddleId || !propertyId) {
      setBiddle(null)
      setProperty(null)
      setError(null)
      setActionError(null)
      return
    }
    if (!signedIn) return

    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const [biddleSnap, propertySnap, ownedSnap] = await Promise.all([
          getDoc(doc(db, 'biddles', biddleId)),
          getDoc(doc(db, 'properties', propertyId)),
          getDocs(
            query(
              collection(db, 'users', user!.uid, 'propertiesOwned'),
              where('propertyID', '==', propertyId),
            ),
          ),
        ])
        if (cancelled) return
        if (!biddleSnap.exists()) {
          setError('Biddle not found')
          setBiddle(null)
          return
        }
        const parsed = parseBiddle(biddleSnap.id, biddleSnap.data() as Record<string, unknown>)
        if (parsed.propertyID && parsed.propertyID !== propertyId) {
          setError('Biddle does not match this property')
          setBiddle(null)
          return
        }
        setBiddle(parsed)
        setProperty(
          propertySnap.exists()
            ? parseProperty(propertySnap.id, propertySnap.data() as Record<string, unknown>)
            : null,
        )
        const buyerTags = profile?.sentBiddles ?? []
        const ownsProperty = !ownedSnap.empty
        const isBuyer = buyerTags.includes(parsed.buyerBiddleTag)
        setIsOwner(ownsProperty && !isBuyer)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load Biddle')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [biddleId, propertyId, user, signedIn, profile?.sentBiddles])

  useEffect(() => {
    if (!biddleId || !propertyId) return
    if (!authLoading && !signedIn) {
      const next = `${location.pathname}?biddle=${encodeURIComponent(biddleId)}&biddleProperty=${encodeURIComponent(propertyId)}`
      openSignIn(navigate, location, next)
    }
  }, [biddleId, propertyId, authLoading, signedIn, navigate, location])

  useEffect(() => {
    if (!biddleId || !propertyId) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeBiddleDetail(navigate)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [biddleId, propertyId, navigate])

  if (!biddleId || !propertyId) return null
  if (!signedIn && !authLoading) return null

  function onClose() {
    closeBiddleDetail(navigate)
  }

  async function onWithdraw() {
    if (!biddle) return
    if (!window.confirm('Withdraw this Biddle? This cannot be undone.')) return
    setBusy(true)
    setActionError(null)
    try {
      await deleteBiddle(biddle.id)
      closeBiddleDetail(navigate)
      if (location.pathname !== '/biddles') {
        navigate('/biddles', { replace: true })
      }
    } catch (e) {
      setActionError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onStartChat() {
    if (!biddle) return
    setBusy(true)
    setActionError(null)
    try {
      await updateDoc(doc(db, 'biddles', biddle.id), {
        ownerHasViewed: true,
        ownerHasAccepted: true,
      })
      const opened: Biddle = { ...biddle, ownerHasViewed: true, ownerHasAccepted: true }
      setBiddle(opened)
      const { channelId } = await createBuyerOwnerChat({
        biddleID: biddle.id,
        biddle: toChatBiddlePayload(opened),
      })
      navigate(`/chat/${channelId}`, { replace: true })
    } catch (e) {
      setActionError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const status = biddle ? biddleStatus(biddle) : null
  const active = biddle ? isBiddleActive(biddle) : false
  const isBuyer = biddle
    ? (profile?.sentBiddles ?? []).includes(biddle.buyerBiddleTag)
    : false

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-navy/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="biddle-detail-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="relative flex max-h-[min(92dvh,800px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
        {/* Out of flow so it never squeezes the header title */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end p-4">
          <button
            type="button"
            onClick={onClose}
            className="pointer-events-auto rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-6">
          {loading || authLoading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <Spinner />
            </div>
          ) : error || !biddle ? (
            <div className="space-y-4 py-8">
              <h2 id="biddle-detail-title" className="text-xl font-bold text-navy">
                Biddle unavailable
              </h2>
              <p className="text-sm text-muted">{error ?? 'We couldn’t find that Biddle.'}</p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="pr-10">
                <p className="text-sm font-medium text-biddle-blue">
                  {isOwner ? 'Incoming Biddle' : 'Your Biddle'}
                </p>
                <h2 id="biddle-detail-title" className="mt-1 text-xl font-bold text-navy">
                  {property ? formatAddressDisplay(property.fullAddress) : 'Property'}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {status ? <Badge tone={status}>{status}</Badge> : null}
                  {!active ? <Badge>Inactive</Badge> : null}
                  <span className="text-sm text-muted">{relativeTime(biddle.timeCreated)}</span>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{formatAud(biddle.price)}</CardTitle>
                  <CardDescription>
                    Settlement {formatSettlement(biddle.settlement)} · Deposit{' '}
                    {formatDeposit(biddle.deposit)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-navy">Notes</p>
                    <p className="mt-1 text-muted">{biddle.notes.trim() || 'No notes provided.'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-navy">Expiry</p>
                    <p className="mt-1 text-muted">
                      {biddle.isOpenOffer
                        ? 'Open offer (no fixed expiry)'
                        : biddle.expiry
                          ? biddle.expiry.toLocaleDateString('en-AU')
                          : 'Not set'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {actionError ? <p className="text-sm text-danger">{actionError}</p> : null}

              {isBuyer && active && !biddle.isDeleted && !biddle.ownerHasAccepted ? (
                <SendLetterPanel
                  biddleId={biddle.id}
                  propertyId={biddle.propertyID || propertyId}
                  propertyAddress={
                    property ? formatAddressDisplay(property.fullAddress) : 'this property'
                  }
                  eligible
                />
              ) : null}

              <div className="flex flex-col gap-2">
                {isBuyer && active && !biddle.isDeleted ? (
                  <Button variant="danger" disabled={busy} onClick={() => void onWithdraw()}>
                    {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                    Withdraw Biddle
                  </Button>
                ) : null}
                {isOwner && active && !biddle.ownerHasAccepted ? (
                  <Button variant="primary" disabled={busy} onClick={() => void onStartChat()}>
                    {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                    Chat with this buyer
                  </Button>
                ) : null}
                {biddle.ownerHasAccepted ? (
                  <Button asChild variant="outline">
                    <Link to="/chat" onClick={onClose}>
                      Open chat
                    </Link>
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
